import { agentInstructionsMaxLength } from '@tunasong/schemas'
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  RadioGroup,
  TextField,
  TunaDialog,
  useTunaMediaQuery,
} from '@tunasong/ui-lib'
import type { ChangeEvent, FC, RefObject } from 'react'
import { useId, useRef, useState } from 'react'
import { personalizeCopy, voiceOptions, type VoiceChoice } from './personalize-copy.js'
import styles from './personalize-dialog.module.css'
import { canSave, defaultForm, isAtDefault, lengthAnnouncement, overBy, type PersonaForm } from './personalize-form.js'

export interface PersonalizeDialogProps {
  open: boolean
  /** What the agent is called now, for the title and the helper text. */
  agentName: string
  /** Its stock name: the Name placeholder and what a blank name means. */
  defaultName: string
  /** The form as the agent stood when the dialog opened (or after "Load theirs"). */
  initial: PersonaForm
  /** Changes when `initial` is replaced, so the form starts again from it. */
  formKey: string
  /** "Everyone in <space> sees these changes." or the personal-item line (Joni §2 Footer). */
  scopeLine: string
  saving?: boolean
  /** A bandmate changed the persona while this dialog was open (Joni §3): Save asks before overwriting. */
  conflict?: boolean
  onCancel: () => void
  onSave: (form: PersonaForm) => void
  onUseMine: (form: PersonaForm) => void
  onLoadTheirs: () => void
}

interface PersonalizeFormProps extends Omit<PersonalizeDialogProps, 'open' | 'formKey'> {
  nameRef: RefObject<HTMLInputElement | null>
}

const PersonalizeFormBody: FC<PersonalizeFormProps> = props => {
  const { agentName, defaultName, initial, scopeLine, saving = false, conflict = false, nameRef } = props
  const { onCancel, onSave, onUseMine, onLoadTheirs } = props
  const [form, setForm] = useState<PersonaForm>(initial)
  const [announcement, setAnnouncement] = useState('')
  const ids = useId()
  const instructionsHelpId = `${ids}-instructions-help`
  const counterId = `${ids}-counter`

  const over = overBy(form)
  const length = form.instructions.trim().length
  const isSaveEnabled = canSave(initial, form) && !saving

  const handleName = (ev: ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: ev.target.value })
  const handleVoice = (_ev: ChangeEvent<HTMLInputElement>, value: string) =>
    setForm({ ...form, voice: value as VoiceChoice })
  const handleInstructions = (ev: ChangeEvent<HTMLInputElement>) => {
    const next = { ...form, instructions: ev.target.value }
    const message = lengthAnnouncement(form.instructions.trim().length, next.instructions.trim().length)
    if (message) {
      setAnnouncement(message)
    }
    setForm(next)
  }

  return (
    <>
      <DialogTitle onClose={onCancel}>{personalizeCopy.title(agentName)}</DialogTitle>
      <DialogContent className={styles.content}>
        <TextField
          label={personalizeCopy.nameLabel}
          value={form.name}
          placeholder={defaultName}
          helperText={personalizeCopy.nameHelper(defaultName)}
          maxLength={40}
          onChange={handleName}
          fullWidth
          // Focus lands here when the dialog opens, ahead of the title's close button (Joni §5)
          ref={nameRef}
        />

        <fieldset className={styles.voice}>
          <legend className={styles.legend}>{personalizeCopy.voiceLegend}</legend>
          <RadioGroup name={`${ids}-voice`} value={form.voice} onChange={handleVoice}>
            {voiceOptions.map(option => (
              <label key={option.value} className={styles.voiceOption}>
                <Radio value={option.value} />
                <span className={styles.voiceText}>
                  <span className={styles.voiceLabel}>{option.label}</span>
                  <span className={styles.voiceDescription}>{option.description}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </fieldset>

        <div className={styles.instructions}>
          <TextField
            label={personalizeCopy.instructionsLabel}
            value={form.instructions}
            placeholder={personalizeCopy.instructionsPlaceholder}
            onChange={handleInstructions}
            multiline
            rows={4}
            fullWidth
            error={over > 0}
            // No maxLength: a hard cap would silently cut a paste (Joni §2)
            aria-describedby={`${instructionsHelpId} ${counterId}`}
          />
          <div className={styles.instructionsFooter}>
            <p id={instructionsHelpId} className={over > 0 ? styles.helperError : styles.helper}>
              {over > 0 ? personalizeCopy.instructionsOver(over) : personalizeCopy.instructionsHelper(agentName)}
            </p>
            <span id={counterId} className={over > 0 ? styles.counterError : styles.counter}>
              {personalizeCopy.counter(length, agentInstructionsMaxLength)}
            </span>
          </div>
          <span className={styles.visuallyHidden} aria-live="polite">
            {announcement}
          </span>
        </div>

        {conflict ? (
          <Alert severity="warning" role="alert">
            <span>{personalizeCopy.conflict(agentName)}</span>
            <span className={styles.conflictActions}>
              <Button size="small" variant="outlined" onClick={() => onUseMine(form)}>
                {personalizeCopy.chooseMine}
              </Button>
              <Button size="small" variant="text" onClick={onLoadTheirs}>
                {personalizeCopy.chooseTheirs}
              </Button>
            </span>
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions className={styles.footer}>
        <p className={styles.scope}>{scopeLine}</p>
        <span className={styles.actions}>
          {isAtDefault(form) ? null : (
            <Button variant="text" onClick={() => setForm(defaultForm)}>
              {personalizeCopy.reset}
            </Button>
          )}
          <Button variant="text" onClick={onCancel}>
            {personalizeCopy.cancel}
          </Button>
          <Button variant="contained" disabled={!isSaveEnabled} onClick={() => onSave(form)}>
            {personalizeCopy.save}
          </Button>
        </span>
      </DialogActions>
    </>
  )
}

/**
 * Personalize an agent (Joni's UX spec §2–§5): its name, voice and "How your band works". Explicit Save,
 * never on every keystroke, because the change is the band's. Full screen on a phone. Saving, the toasts
 * and the concurrent-edit check belong to the caller (`usePersonalizeAgent`).
 */
export const PersonalizeDialog: FC<PersonalizeDialogProps> = props => {
  const { open, formKey, onCancel, ...formProps } = props
  const isPhone = useTunaMediaQuery('(max-width: 599.95px)')
  const nameRef = useRef<HTMLInputElement>(null)

  return (
    <TunaDialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      aria-label={personalizeCopy.title(props.agentName)}
      initialFocus={nameRef}
    >
      {open ? <PersonalizeFormBody key={formKey} {...formProps} onCancel={onCancel} nameRef={nameRef} /> : null}
    </TunaDialog>
  )
}
